import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import {
  type RecentlyViewedPursuit,
  recentlyViewedPursuitsQueryKeys,
} from '@/hooks/recently-viewed/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'

export type RecentlyViewedPursuitsView = 'all' | 'proposal' | 'active'

interface UseRecentlyViewedPursuitsProps<T> {
  orgId: UUID | ''
  view?: RecentlyViewedPursuitsView
  select?: (pursuits: PaginatedResponse<RecentlyViewedPursuit>) => T
}

export const useRecentlyViewedPursuits = <T = RecentlyViewedPursuit[]>({
  orgId,
  view = 'all',
  select: selectFn,
}: UseRecentlyViewedPursuitsProps<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const select = useCallback(
    (pursuits: PaginatedResponse<RecentlyViewedPursuit>) => {
      if (selectFn) {
        return selectFn(pursuits)
      }
      const data = pursuits.data
      if (view === 'proposal') {
        return data.filter((pursuit) => pursuit.has_proposal_content)
      }
      if (view === 'active') {
        return data.filter((pursuit) => {
          const dueDate = new Date(pursuit.due_date)
          const isActive = dueDate >= new Date()
          return isActive
        })
      }
      return data
    },
    [view, selectFn],
  )

  return useQuery({
    queryKey: recentlyViewedPursuitsQueryKeys.organization({ orgId }),
    queryFn: () =>
      fetchRecentlyViewedPursuits(orgId, accessToken, { limit: 10 }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    select,
  })
}

export const fetchRecentlyViewedPursuits = async (
  orgId: UUID | '',
  accessToken: string,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<RecentlyViewedPursuit>> => {
  if (!orgId) {
    throw new Error('Org ID is required')
  }

  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits/recently-viewed`,
    pagination,
  )
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to fetch recently viewed pursuits: ${response.statusText}`,
      cause: response,
    })
  }

  return (await response.json()) as PaginatedResponse<RecentlyViewedPursuit>
}

/**
 * Hook to optimistically move a pursuit to the top of the recently viewed list.
 * Useful when clicking on a pursuit to immediately reflect it as the most recent.
 */

interface UseMoveToTopOfRecentlyViewedPursuitsProps {
  orgId: UUID | ''
}

export const useMoveToTopOfRecentlyViewedPursuits = ({
  orgId,
}: UseMoveToTopOfRecentlyViewedPursuitsProps) => {
  const queryClient = useQueryClient()

  return useCallback(
    (pursuitId: UUID) => {
      queryClient.setQueryData<PaginatedResponse<RecentlyViewedPursuit>>(
        recentlyViewedPursuitsQueryKeys.organization({ orgId }),
        (oldData) => {
          if (!oldData) return oldData
          const clickedIndex = oldData.data.findIndex((p) => p.id === pursuitId)
          if (clickedIndex <= 0) return oldData // Already at top or not found
          const newData = [...oldData.data]
          const [clicked] = newData.splice(clickedIndex, 1)
          clicked.last_viewed_at = new Date().toISOString()
          newData.unshift(clicked)
          return { ...oldData, data: newData }
        },
      )
    },
    [queryClient, orgId],
  )
}
